import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  const getUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, full_name, email, avatar_url, created_at')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };


  const signUp = async (email, password, userData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: userData.username,
            full_name: userData.full_name
          },
          emailRedirectTo: window.location.origin
        }
      });

      if (error) throw error;

      // Crear perfil inmediatamente si el usuario se crea
      if (data.user) {
        try {
          await supabase
            .from('users')
            .insert({
              id: data.user.id,
              username: userData.username,
              full_name: userData.full_name,
              email: email,
              avatar_url: userData.avatar_url || null,
              created_at: new Date().toISOString()
            });
        } catch (profileError) {
          console.error('Error creando perfil:', profileError);
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      console.log('Iniciando cierre de sesión...');

      // Limpiar el estado inmediatamente
      setUser(null);
      setUserProfile(null);

      // Cerrar la sesión en Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error en signOut de Supabase:', error);
      }

      // Limpiar localStorage por si acaso
      localStorage.removeItem('supabase.auth.token');

      console.log('Sesión cerrada exitosamente');

      // Opcional: forzar recarga de la página para limpiar todo
      setTimeout(() => {
        window.location.reload();
      }, 100);

    } catch (error) {
      console.error('Error cerrando sesión:', error);
      // Aun así, limpiar el estado local
      setUser(null);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    const getSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Error obteniendo sesión:', sessionError);
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            setInitialCheckDone(true);
          }
          return;
        }

        if (!mounted) return;

        setUser(session?.user ?? null);

        if (session?.user) {
          const profile = await getUserProfile(session.user.id);
          if (mounted) {
            setUserProfile(profile);
            setLoading(false);
            setInitialCheckDone(true);
          }
        } else {
          if (mounted) {
            setUserProfile(null);
            setLoading(false);
            setInitialCheckDone(true);
          }
        }
      } catch (error) {
        console.error('Error en getSession:', error);
        if (mounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          setInitialCheckDone(true);
        }
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          if (!mounted) return;

          // Solo responder a eventos de cierre de sesión
          if (event === 'SIGNED_OUT') {
            setUser(null);
            setUserProfile(null);
          } else if (event === 'SIGNED_IN') {
            setUser(session?.user ?? null);
            // NO recargar el perfil, ya se cargó en getSession
          }
        } catch (error) {
          console.error('Error en auth state change:', error);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const updateUserProfile = async () => {
    if (user) {
      const profile = await getUserProfile(user.id);
      setUserProfile(profile);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};